'use client'

import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import { useEffect, useState } from 'react'
import { useMediaQuery } from '@mantine/hooks'
import Slider, { Settings } from 'react-slick'
import { reviewSchedule } from '../_data/ktechpioneers'
import { AnimatedItem } from './AnimatedSection'
import styles from '../ktechpioneers.module.scss'

function useWindowWidth() {
  const [width, setWidth] = useState(0)
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth)
    onResize()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  return width
}

export default function ReviewScheduleSlider() {
  const isCustom = useMediaQuery('(max-width: 1279px)')
  const windowWidth = useWindowWidth()

  if (isCustom === undefined) return null

  // PC: 기존 flex 레이아웃
  if (!isCustom) {
    return (
      <div className="flex w-full justify-center flex-row items-stretch gap-[61px]">
        {reviewSchedule.map(review => (
          <ReviewCard key={review.title} review={review} />
        ))}
      </div>
    )
  }

  // 모바일: 슬라이드
  const settings: Settings = {
    infinite: false,
    slidesToShow: 1.1,
    slidesToScroll: 1,
    speed: 500,
    dots: false,
    arrows: true,
    variableWidth: true,
    touchMove: true,
    centerMode: true,
    centerPadding: '0px',
    initialSlide: 0,
    responsive: [
      {
        breakpoint: 1280,
        settings: {
          slidesToShow: 1.1,
          slidesToScroll: 1,
          dots: false,
          arrows: true,
          infinite: false,
          speed: 500,
          centerMode: false,
          touchMove: true,
          variableWidth: false,
        },
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 1.1,
          slidesToScroll: 1,
          dots: false,
          arrows: true,
          infinite: false,
          speed: 500,
          centerMode: false,
          touchMove: true,
          variableWidth: false,
        },
      },
      {
        breakpoint: 800,
        settings: {
          slidesToShow: 1.1,
          slidesToScroll: 1,
          dots: false,
          arrows: true,
          infinite: false,
          speed: 500,
          centerMode: false,
          touchMove: true,
          variableWidth: false,
        },
      },
      {
        breakpoint: 680,
        settings: {
          slidesToShow: 1.1,
          slidesToScroll: 1,
          dots: false,
          arrows: true,
          infinite: false,
          speed: 500,
          centerMode: false,
          touchMove: true,
          variableWidth: false,
        },
      },
      {
        breakpoint: 560,
        settings: {
          slidesToShow: 1.1,
          slidesToScroll: 1,
          dots: false,
          arrows: true,
          infinite: false,
          speed: 500,
          centerMode: false,
          touchMove: true,
          variableWidth: false,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1.1,
          slidesToScroll: 1,
          dots: false,
          arrows: true,
          infinite: false,
          speed: 500,
          centerMode: false,
          touchMove: true,
          variableWidth: false,
        },
      },
    ],
  }

  return (
    <Slider key={windowWidth} {...settings}>
      {reviewSchedule.map(review => (
        <div key={review.title} className="pb-[68px]">
          <ReviewCard review={review} />
        </div>
      ))}
    </Slider>
  )
}

function ReviewCard({ review }: { review: (typeof reviewSchedule)[number] }) {
  return (
    <AnimatedItem className="relative group pr-[25px] pc:pr-0 flex flex-1 h-full">
      <div
        className="flex h-full flex-col gap-4 w-full overflow-hidden rounded-xl border border-white/10 bg-[rgba(255,255,255,0.05)] backdrop-blur-[2px] p-4 pc:gap-[17px] pc:p-5">
        <div className="flex w-full items-center gap-2 border-b border-white/50 pb-4 pc:gap-3 pc:pb-6">
          <span className="h-[13px] w-[13px] bg-[#3385FF]" />
          <h5 className="text-lg font-strong text-[#3385FF] pc:text-2xl">
            {review.title}
          </h5>
        </div>
        <div className="w-full">
          <img src={review.imgUrl} alt={review.title} className="w-full h-auto object-contain" />
        </div>
        <div className="flex w-full flex-col items-center justify-start gap-[17px]">
          {review.typeList && (
            <div className="flex w-full flex-row items-center justify-start gap-1">
              {review.typeList.map(type => (
                <span
                  key={type}
                  className="rounded-full max-w-fit bg-[rgba(51,133,255,0.20)] px-3 py-0.5 text-xs text-[#75ADFF] pc:text-base">
                  {type}
                </span>
              ))}
            </div>
          )}
          <div className="flex flex-col items-start justify-start gap-2 w-full">
            {review.desc && (
              <p className="text-base whitespace-pre-line text-left font-semibold break-keep text-white pc:text-lg">
                {review.desc}
              </p>
            )}
            {review.descList && (
              <ul className="flex flex-col w-full">
                {review.descList.map(item => (
                  <li key={item}
                    className={`${styles.text_dot} ${styles.text_dot_reviewschedule} text-sm whitespace-pre-line break-keep text-[#C9C9C9] pc:text-lg`}>{item}</li>
                ))}
              </ul>
            )}
          </div>
          <div className="flex items-start gap-2 flex-row w-full border-t border-white/20 pt-[17px]">
            <img src={`https://glob-dev-resource.s3.ap-northeast-2.amazonaws.com/course/events/ktechpioneers/icon_review_inner_arrow.png`} alt="arrow" className="w-3 h-auto object-contain" />
            <p className="text-base break-keep pc:text-lg text-white">
              {review.summary}
            </p>
          </div>
        </div>
      </div>
      <img src={`https://glob-dev-resource.s3.ap-northeast-2.amazonaws.com/course/events/ktechpioneers/icon_review_card_arrow.png`} alt="arrow" className={`${styles.review_card_arrow_arrow} absolute pc:group-last:hidden top-1/2 -translate-y-1/2 right-[7px] pc:-right-[39px] w-[9px] pc:w-[18px] h-auto object-contain`} />
    </AnimatedItem>
  )
}
